import { type UseQueryResult, useQueries } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { tagRows } from "./networkMath";
import { usePbjNetwork } from "./PbjNetworkContext";

/** A row that remembers which building it came from (the department whose endpoint returned it). */
export type WithBuilding<Row> = Row & { buildingId: string; buildingName: string };

export type BuildingResult<Data> = { departmentId: string; data: Data };

/** The fields every PBJ table and badge reads off a query. */
export type BuildingQueryState<Data> = { data: Data | undefined; isLoading: boolean; isError: boolean };

/** One building's query. Same shape as the single-building `useQuery` options it replaces. */
export type BuildingQueryOptions<Data> = {
	queryKey: readonly unknown[];
	queryFn: () => Promise<Data>;
	enabled: boolean;
	staleTime: number;
	refetchOnMount?: boolean;
	refetchOnWindowFocus?: boolean;
	refetchOnReconnect?: boolean;
};

/**
 * Runs `queryFor` once per building the PBJ data tabs read (only the header department outside the
 * network view). Each building keeps its single-building query key, so switching the network view on
 * and off reuses the cache. `data` stays undefined until every building has answered, so a table
 * never shows a partial network as if it were whole; any failed building is an error.
 */
export function useBuildingResults<Data>(
	queryFor: (departmentId: string) => BuildingQueryOptions<Data>,
	enabled: boolean,
): BuildingQueryState<BuildingResult<Data>[]> {
	const { buildings } = usePbjNetwork();
	const departmentIds = useMemo(() => buildings.map((building) => building.departmentId), [buildings]);
	const combine = useCallback(
		(results: UseQueryResult<Data>[]): BuildingQueryState<BuildingResult<Data>[]> => {
			const loaded = results.flatMap((result, index) => (result.data === undefined ? [] : [{ departmentId: departmentIds[index], data: result.data }]));
			return {
				data: loaded.length === results.length ? loaded : undefined,
				isLoading: results.some((result) => result.isLoading),
				isError: results.some((result) => result.isError),
			};
		},
		[departmentIds],
	);
	return useQueries({
		queries: departmentIds.map((departmentId) => {
			const options = queryFor(departmentId);
			return { ...options, enabled: enabled && options.enabled };
		}),
		combine,
	});
}

/** `useBuildingResults` for endpoints that return a bare row list: one combined, building-tagged list. */
export function useBuildingRows<Row>(
	queryFor: (departmentId: string) => BuildingQueryOptions<Row[]>,
	enabled: boolean,
): BuildingQueryState<WithBuilding<Row>[]> {
	const { buildingName } = usePbjNetwork();
	const { data, isLoading, isError } = useBuildingResults(queryFor, enabled);
	const rows = useMemo(() => (data ? tagRows(data, buildingName) : undefined), [data, buildingName]);
	return useMemo(() => ({ data: rows, isLoading, isError }), [rows, isLoading, isError]);
}
